/*
 * GuestDashboard.jsx
 * ------------------
 * Dashboard for guest users. Same layout as Dashboard but with:
 * - A sticky warning banner about nightly data clearing
 * - Lock prompts replacing authenticated-only features
 * - No stats, no streaks, no notifications
 * - A prominent sign in nudge throughout
 * Fetches checklists using the guest ID from localStorage.
 * Consumes AuthContext.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useToast } from "../App";
import ChecklistItem from "../components/ChecklistItem";
import LockPrompt from "../components/LockPrompt";

// ============================================
// HELPERS -- reused from Dashboard
// ============================================
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function getPriorityWeight(priority) {
  const weights = { high: 0, medium: 1, low: 2 };
  return weights[priority] ?? 2;
}

function categorizeItems(checklists) {
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  const next7Days = new Date(now);
  next7Days.setDate(now.getDate() + 7);

  const overdue = [];
  const today = [];
  const upcoming = [];
  const noDate = [];

  for (const checklist of checklists) {
    for (const item of checklist.items || []) {
      if (item.checked) continue;
      const enriched = { ...item, checklistTitle: checklist.title, checklistId: checklist.id };

      if (!item.dueDate) {
        noDate.push(enriched);
        continue;
      }

      const due = new Date(item.dueDate);
      if (due < todayStart) overdue.push(enriched);
      else if (due >= todayStart && due <= todayEnd) today.push(enriched);
      else if (due > todayEnd && due <= next7Days) upcoming.push(enriched);
    }
  }

  const sortByPriority = (a, b) => getPriorityWeight(a.priority) - getPriorityWeight(b.priority);
  const sortByDateThenPriority = (a, b) => {
    const dateDiff = new Date(a.dueDate) - new Date(b.dueDate);
    return dateDiff !== 0 ? dateDiff : sortByPriority(a, b);
  };

  overdue.sort(sortByDateThenPriority);
  today.sort(sortByPriority);
  upcoming.sort(sortByDateThenPriority);
  noDate.sort(sortByPriority);

  return { overdue, today, upcoming, noDate };
}

// ============================================
// GUEST WARNING BANNER
// Sticky at top, always visible
// ============================================
function GuestBanner() {
  return (
    <div style={{
      background: "var(--accent-gold-muted)",
      border: "1px solid var(--accent-gold)",
      borderRadius: "var(--radius-md)",
      padding: "var(--space-3) var(--space-4)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "var(--space-3)",
      flexWrap: "wrap"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        <span style={{ fontSize: "18px" }}>⚠️</span>
        <div>
          <p style={{
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: "14px",
            color: "var(--accent-gold)",
            margin: "0 0 2px"
          }}>
            You are using Checklist as a guest
          </p>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "13px",
            color: "var(--text-secondary)",
            margin: 0
          }}>
            Your data will be cleared tonight at 2 AM UTC. Sign in to save permanently.
          </p>
        </div>
      </div>
      <button
        onClick={() => window.location.href = "/.auth/login/aad"}
        style={{
          background: "var(--accent-gold)",
          border: "none",
          borderRadius: "var(--radius-sm)",
          padding: "var(--space-2) var(--space-4)",
          cursor: "pointer",
          fontFamily: "var(--font-body)",
          fontWeight: 600,
          fontSize: "13px",
          color: "white",
          whiteSpace: "nowrap",
          transition: "opacity var(--transition-fast)"
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
        onMouseLeave={e => e.currentTarget.style.opacity = "1"}
      >
        Sign In
      </button>
    </div>
  );
}

// ============================================
// SECTION HEADER
// ============================================
function SectionHeader({ title, count, color, collapsed, onToggle }) {
  if (count === 0) return null;

  return (
    <button
      onClick={onToggle}
      style={{
        width: "100%",
        background: "none",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "var(--space-3) 0",
        borderBottom: "2px solid " + color
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        <h2 style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: "20px",
          color,
          margin: 0
        }}>
          {title}
        </h2>
        <span style={{
          fontFamily: "var(--font-body)",
          fontSize: "13px",
          fontWeight: 600,
          color: "white",
          background: color,
          borderRadius: "100px",
          padding: "2px 8px"
        }}>
          {count}
        </span>
      </div>
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        style={{
          color,
          transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
          transition: "transform var(--transition-base)"
        }}
      >
        <path d="M3 6L8 11L13 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}

// ============================================
// GUEST DASHBOARD SECTION
// ============================================
function GuestSection({ title, items, color, onCheckItem }) {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  if (items.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <SectionHeader
        title={title}
        count={items.length}
        color={color}
        collapsed={collapsed}
        onToggle={() => setCollapsed(prev => !prev)}
      />
      {!collapsed && (
        <div style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          overflow: "hidden"
        }}>
          {items.map(item => (
            <div key={item.id}>
              <ChecklistItem
                item={item}
                onCheck={onCheckItem}
                readOnly={false}
                onUpdate={() => {}}
                onDelete={() => {}}
              />
              <div
                onClick={() => navigate("/checklists/" + item.checklistId)}
                style={{ padding: "2px var(--space-4) var(--space-2)", cursor: "pointer" }}
              >
                <span style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "11px",
                  color: "var(--text-muted)",
                  fontStyle: "italic"
                }}>
                  in {item.checklistTitle} →
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// LOCKED FEATURE CARDS
// Shows what guests are missing in a clean row
// ============================================
function LockedFeatures() {
  const [activeLock, setActiveLock] = useState(null);

  const features = [
    { id: "streaks", icon: "🔥", label: "Streaks", description: "Build daily habits and track your consistency across checklists." },
    { id: "recurring", icon: "🔄", label: "Recurring", description: "Set checklists to reset daily, weekly, or monthly automatically." },
    { id: "email", icon: "📧", label: "Email Digest", description: "Get a daily email with your overdue, today, and upcoming items." },
    { id: "mirror", icon: "🪞", label: "Smart Mirror", description: "Display your checklist on a smart mirror or secondary screen." },
  ];

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-3)"
    }}>
      <p style={{
        fontFamily: "var(--font-body)",
        fontSize: "12px",
        fontWeight: 600,
        color: "var(--text-muted)",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        margin: 0
      }}>
        Unlock with a free account
      </p>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: "var(--space-3)"
      }}>
        {features.map(feature => (
          <button
            key={feature.id}
            onClick={() => setActiveLock(feature.id)}
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)",
              padding: "var(--space-4)",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "var(--space-2)",
              transition: "border-color var(--transition-fast), box-shadow var(--transition-fast)"
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = "var(--accent-gold)";
              e.currentTarget.style.boxShadow = "var(--shadow-sm)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = "var(--border)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <span style={{ fontSize: "24px" }}>{feature.icon}</span>
            <span style={{
              fontFamily: "var(--font-body)",
              fontSize: "13px",
              fontWeight: 600,
              color: "var(--text-primary)"
            }}>
              {feature.label}
            </span>
            <span style={{
              fontFamily: "var(--font-body)",
              fontSize: "11px",
              color: "var(--accent-gold)",
              fontWeight: 600
            }}>
              Sign in →
            </span>
          </button>
        ))}
      </div>

      {activeLock && (
        <LockPrompt
          variant="modal"
          feature={features.find(f => f.id === activeLock)?.label || "This feature"}
          description={features.find(f => f.id === activeLock)?.description}
          onClose={() => setActiveLock(null)}
        />
      )}
    </div>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function GuestDashboard() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);

  // ============================================
  // FETCH CHECKLISTS
  // ============================================
  const fetchChecklists = useCallback(async () => {
    try {
      const res = await fetch("/api/checklists", {
        headers: {
          "Content-Type": "application/json",
          "x-guest-id": user?.id || ""
        }
      });
      const data = await res.json();
      setChecklists(data);
    } catch (err) {
      console.error("Failed to fetch guest checklists", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  // ============================================
  // CHECK OFF ITEM
  // ============================================
  async function handleCheckItem(item) {
    const originalChecklists = checklists;

    setChecklists(prev => prev.map(checklist => {
      if (checklist.id !== item.checklistId) return checklist;
      return {
        ...checklist,
        items: checklist.items.map(i =>
          i.id === item.id ? { ...i, checked: true } : i
        )
      };
    }));

    showToast("Item checked off", () => {
  setChecklists(originalChecklists);
});

    try {
      const checklist = checklists.find(c => c.id === item.checklistId);
      if (!checklist) return;

      const updatedItems = checklist.items.map(i =>
        i.id === item.id ? { ...i, checked: true } : i
      );

      await fetch("/api/checklists", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-guest-id": user?.id || ""
        },
        body: JSON.stringify({
          id: checklist.id,
          title: checklist.title,
          items: updatedItems,
          category: checklist.category,
          recurring: false
        })
      });
    } catch (err) {
      console.error("Failed to update item", err);
    }
  }

  const { overdue, today, upcoming, noDate } = categorizeItems(checklists);
  const hasItems = overdue.length > 0 || today.length > 0 || upcoming.length > 0 || noDate.length > 0;

  if (loading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh"
      }}>
        <p style={{ fontFamily: "var(--font-body)", color: "var(--text-muted)" }}>
          Loading...
        </p>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: "800px",
      margin: "0 auto",
      padding: "var(--space-6) var(--space-4)",
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-6)"
    }}>

      {/* Guest warning banner */}
      <GuestBanner />

      {/* Greeting */}
      <div>
        <h1 style={{
          fontFamily: "var(--font-display)",
          fontWeight: 900,
          fontSize: "clamp(28px, 5vw, 40px)",
          color: "var(--text-primary)",
          margin: "0 0 var(--space-2)",
          letterSpacing: "-1px"
        }}>
          {getGreeting()}, Guest.
        </h1>
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "15px",
          color: "var(--text-muted)",
          margin: 0
        }}>
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric"
          })}
        </p>
      </div>

      {/* Locked features */}
      <LockedFeatures />

      {/* Sections */}
      {hasItems ? (
        <>
          <GuestSection
            title="Overdue"
            items={overdue}
            color="var(--status-overdue)"
            onCheckItem={handleCheckItem}
          />
          <GuestSection
            title="Today"
            items={today}
            color="var(--status-today)"
            onCheckItem={handleCheckItem}
          />
          <GuestSection
            title="Upcoming"
            items={upcoming}
            color="var(--status-upcoming)"
            onCheckItem={handleCheckItem}
          />
          <GuestSection
            title="No Date"
            items={noDate}
            color="var(--text-muted)"
            onCheckItem={handleCheckItem}
          />
        </>
      ) : (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--space-8)",
          gap: "var(--space-4)",
          textAlign: "center"
        }}>
          <span style={{ fontSize: "64px" }}>📋</span>
          <h2 style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "24px",
            color: "var(--text-primary)",
            margin: 0
          }}>
            No items yet
          </h2>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "15px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            Create your first checklist to get started.
          </p>
          <button
            onClick={() => navigate("/checklists")}
            style={{
              background: "var(--accent-green)",
              border: "none",
              borderRadius: "var(--radius-md)",
              padding: "var(--space-3) var(--space-6)",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15px",
              color: "white",
              transition: "opacity var(--transition-fast)"
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            Create a Checklist
          </button>
        </div>
      )}
    </div>
  );
}